import { AxiosResponse } from "axios";
import { useMemo } from "react";
import {
  MutationFunction,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "react-query";

import Repository, {
  ExtendedAxiosRequestConfig,
  ExtractSerializer,
} from "../abstracts/Repository";
import { Identifier, PaginatorContract, UrlInjectionMap } from "../types";
import { QueryKeyManager } from "./utils/queryKeyManager";
import { createQueryOptions } from "./utils/react-query-options";

export type UseGenericPaginatedQueryOptionsType<T> = Omit<
  UseInfiniteQueryOptions<
    PaginatorContract<T>, // Data type
    unknown, // Error type
    PaginatorContract<T>, // Data type returned by the query function
    PaginatorContract<T>, // Data type returned by the query function
    string[] // Type of the query key
  >,
  "queryKey" | "queryFn"
>;

/**
 * Provides hooks for managing and interacting with a specific repository.
 * This service leverages react-query for caching, synchronization, and state management of server state.
 * Each hook within the service handles specific data fetching or mutation tasks and includes built-in
 * error handling and success callbacks to manage side effects such as cache invalidation.
 *
 * Example Usage:
 * ```
 * const { useList, useCreate } = useModelService(myRepository);
 * const { data, error, isLoading } = useList();
 * const { mutate: createItem } = useCreate();
 *
 * if (isLoading) return <LoadingIndicator />;
 * if (error) return <ErrorDisplay message={error.message} />;
 *
 * return (
 *   <div>
 *     {data.map(item => <div key={item.id}>{item.name}</div>)}
 *     <button onClick={() => createItem({ name: 'New Item' })}>
 *       Add Item
 *     </button>
 *   </div>
 * );
 * ```
 *
 * @param {RepositoryContract<T>} repository - The repository instance associated with specific model operations,
 * typically defined per model type within an application's domain.
 *
 * Response Handling:
 * Each hook returns various properties based on its type:
 * - Query hooks (useList, useRetrieve, useGenericQuery) return an object containing
 *   { data, error, isLoading, refetch }. These can be used to render UI components based on the data's loading state,
 *   handle errors, or trigger data re-fetching.
 * - Mutation hooks (useCreate, useUpdate, useDelete, useGenericMutation) return a mutation object with methods
 *   like mutate and mutateAsync, allowing for command execution with optional optimistic updates to the UI.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Let TS infer, whereas `never` is misaligned to T
export function useModelService<R extends Repository<any>>(repository: R) {
  type T = ExtractSerializer<R>;

  const queryKeyManager = new QueryKeyManager(repository);
  const queryClient = useQueryClient();

  const invalidateAllRepositoryQueries = () => {
    const queryKey = queryKeyManager.getBaseKey();
    queryClient.invalidateQueries(queryKey);
  };

  const removeAllRepositoryQueries = () => {
    const queryKey = queryKeyManager.getBaseKey();
    queryClient.removeQueries(queryKey);
  };

  const invalidateSubcategoryQueries = (subcategory: string) => {
    const queryKey = queryKeyManager.generateKey([subcategory]);
    queryClient.invalidateQueries(queryKey);
  };

  const invalidateQueriesByRepositoryNames = (
    repositoryNames: readonly string[]
  ): void => {
    repositoryNames.forEach((name) => {
      queryClient.invalidateQueries(name);
    });
  };

  function useList({
    params,
    config,
    options,
  }: QueryServiceOptionsFactory<T[]> = {}) {
    const queryKey = queryKeyManager.generateKey([
      "list",
      JSON.stringify({ ...repository.getInjects() }),
      JSON.stringify(params),
    ]);
    const queryOptions = createQueryOptions({
      ...options,
    });
    const axiosConfig = { ...config }; // Copy the config object to avoid mutation

    return useQuery(
      queryKey,
      () => repository.list({ ...axiosConfig, params }),
      queryOptions
    );
  }

  function useCreate<S = T>({
    config,
    options,
  }: MutationServiceOptionsFactory<unknown, S> = {}) {
    const mutationFn: MutationFunction<unknown, S> = (newData: S) =>
      repository.create(newData, config);

    return useMutation(mutationFn, {
      onSuccess: () => {
        invalidateAllRepositoryQueries();
      },
      ...options,
    });
  }

  function useRetrieve(
    id: Identifier | undefined,
    { config, options }: QueryServiceOptionsFactory<T> = {}
  ) {
    const isEnabled = () => {
      if (Boolean(!!id && id !== "undefined") === false) {
        return false;
      }
      if (options && "enabled" in options) {
        return Boolean(options.enabled);
      }
      return true;
    };
    const queryKey = queryKeyManager.generateKey([
      "retrieve",
      id === undefined || id === "undefined" ? "undefined" : id,
    ]);
    const queryOptions = createQueryOptions({
      ...options,
      enabled: isEnabled(),
    });

    return useQuery(
      queryKey,
      () => repository.retrieve(id, config),
      queryOptions
    );
  }

  function useUpdate({
    config,
    options,
  }: MutationServiceOptionsFactory<
    unknown,
    { id: Identifier; newData: T }
  > = {}) {
    return useMutation(
      (updateData: { id: Identifier; newData: T }) =>
        repository.update(updateData.id, updateData.newData, config),
      {
        onSuccess: () => {
          invalidateSubcategoryQueries("retrieve");
        },
        ...options,
      }
    );
  }

  function usePartialUpdate({
    config,
    options,
  }: MutationServiceOptionsFactory<
    unknown,
    { id: Identifier; newData: Partial<T> }
  > = {}) {
    return useMutation(
      (updateData: { id: Identifier; newData: Partial<T> }) =>
        repository.partialUpdate(updateData.id, updateData.newData, config),
      {
        onSuccess: () => {
          invalidateSubcategoryQueries("retrieve");
        },
        ...options,
      }
    );
  }

  function useDelete({
    config,
    options,
  }: {
    config?: ExtendedAxiosRequestConfig;
    options?: UseMutationOptions<unknown, unknown, Identifier, unknown>;
  } = {}) {
    const mutationFn: MutationFunction<unknown, Identifier> = (id) =>
      repository.destroy(id, config);

    return useMutation(mutationFn, {
      // After a successful mutation:
      // The specific retrieve query is removed from cache (preventing any refetch attempts)
      // List views are properly refreshed to show the updated data
      // You won't get errors from trying to fetch a deleted item
      onSuccess: () => {
        const retrieveQueryKey = queryKeyManager.generateKey(["retrieve"]);
        queryClient.removeQueries(retrieveQueryKey);
        invalidateSubcategoryQueries("list");
        invalidateSubcategoryQueries("paginate");
      },

      ...options,
    });
  }

  function usePaginated({
    url = "paginate",
    injects,
    params,
    config,
    options,
  }: {
    url?: "list" | "paginate" | string;
    injects?: UrlInjectionMap;
    params?: Record<string, unknown>;
    config?: ExtendedAxiosRequestConfig;
    options?: Omit<
      UseInfiniteQueryOptions<
        PaginatorContract<T>, // Data type
        unknown, // Error type
        PaginatorContract<T>, // Data type returned by the query function
        PaginatorContract<T>, // Data type returned by the query function
        string[] // Type of the query key
      >,
      "queryKey" | "queryFn"
    >;
  } = {}): UseInfiniteQueryResult<PaginatorContract<T>> & {
    hasNoResults: boolean;
  } {
    const axiosConfig = { ...config };
    const queryKey = queryKeyManager.generateKey(
      params ? [url, JSON.stringify(params)] : [url],
      injects
    );

    // Use createQueryOptions to create query options with correct types
    const queryOptions = createQueryOptions<
      UseInfiniteQueryOptions<
        PaginatorContract<T>,
        unknown,
        PaginatorContract<T>,
        PaginatorContract<T>,
        string[]
      >
    >({
      getNextPageParam(lastPage) {
        return lastPage?.next_page_number || undefined;
      },
      ...options,
    });

    const { isFetching, hasNextPage, fetchNextPage, ...rest } =
      useInfiniteQuery(
        queryKey,
        ({ pageParam = 1 }) => {
          const queryParams = { ...params, page: pageParam };
          return repository
            .request(repository.buildUrl(url, injects), {
              ...axiosConfig,
              params: queryParams,
            })
            .then((response) => response.data);
        },
        queryOptions
      );

    const hasNoResults = useMemo(
      () => !isFetching && rest.data?.pages[0]?.results?.length === 0,
      [isFetching, rest.data]
    );

    return { isFetching, hasNextPage, fetchNextPage, hasNoResults, ...rest };
  }

  /**
   * Hook to fetch data with react-query and manage cache invalidation.
   *
   * @param {string} url - The URL to fetch data from.
   * @param {Object} configuration - Additional configuration options for the mutation.
   */
  function useGenericQuery<T>(
    url: string,
    { injects, params, config, options }: QueryServiceOptionsFactory<T> = {}
  ) {
    const keyParams = params ? [url, JSON.stringify(params)] : [url];
    if (config?.data) {
      keyParams.push(JSON.stringify(config.data));
    }
    // Generate a unique query key based on the URL and parameters
    const queryKey = queryKeyManager.generateKey(keyParams, injects);

    const axiosConfig = { ...config }; // Copy the config object to avoid mutation
    const queryOptions = createQueryOptions<
      UseQueryOptions<T, unknown, T, QueryKey>
    >({
      ...options,
    });

    // Fetch data using useQuery and pass config options
    return useQuery(
      queryKey,
      // Request data from repository and return response data
      () =>
        repository
          .request(repository.buildUrl(url, injects), {
            ...axiosConfig,
            params,
          })
          .then((response) => response.data),
      queryOptions
    );
  }

  /**
   * Hook to perform mutation operations with react-query and manage cache invalidation.
   *
   * @param url - URL key from the repository `urlMap` (passed to `buildUrl`).
   * @param method - HTTP verb. Use `GET` with `mapVariablesToParams` for query-style calls (e.g. gateway lookup).
   * @param configuration - `config`, `options`, and optional variable mappers (`mapVariablesToUrlInjects`, `mapVariablesToRequestBody`, `mapVariablesToParams`).
   */
  function useGenericMutation<TData = T, TVariables = T>(
    url: string,
    method: "POST" | "PUT" | "DELETE" | "PATCH" | "GET",
    {
      injects,
      config,
      options,
      mapVariablesToUrlInjects,
      mapVariablesToRequestBody,
      mapVariablesToParams,
    }: MutationServiceOptionsFactory<AxiosResponse<TData>, TVariables> = {}
  ) {
    const usesVariableMapping =
      mapVariablesToUrlInjects !== undefined ||
      mapVariablesToParams !== undefined ||
      mapVariablesToRequestBody !== undefined;

    const mutationFn = usesVariableMapping
      ? (variables: TVariables) => {
          const requestConfig: ExtendedAxiosRequestConfig = {
            method,
            ...config,
          };
          if (mapVariablesToRequestBody !== undefined) {
            requestConfig.data = mapVariablesToRequestBody(variables);
          }
          if (mapVariablesToParams !== undefined) {
            const existing = config?.params;
            const baseParams =
              existing &&
              typeof existing === "object" &&
              !Array.isArray(existing) &&
              !(existing instanceof URLSearchParams)
                ? { ...(existing as Record<string, unknown>) }
                : {};
            requestConfig.params = {
              ...baseParams,
              ...mapVariablesToParams(variables),
            };
          }
          return repository.request(
            repository.buildUrl(url, {
              ...injects,
              ...(mapVariablesToUrlInjects?.(variables) ?? {}),
            }),
            requestConfig
          );
        }
      : (data: unknown, newInjects?: UrlInjectionMap) =>
          // Request mutation operation from repository and include method, data, and additional config
          repository.request(
            repository.buildUrl(url, { ...injects, ...newInjects }),
            {
              method,
              data,
              ...config,
            }
          );

    // Perform mutation using useMutation and pass config options
    return useMutation<AxiosResponse<TData>, unknown, TVariables>(
      mutationFn as (variables: TVariables) => Promise<AxiosResponse<TData>>,
      {
        // Handle onSuccess callback to invalidate related queries
        onSuccess: () => {
          invalidateSubcategoryQueries(url);
        },
        ...options,
      }
    );
  }

  /**
   * Hook to fetch data with react-query and add pagination.
   *
   * @param {string} url - The URL for the mutation operation.
   */
  function useGenericPaginatedQuery<S = T>(
    url: string,
    {
      injects,
      params,
      config,
      options,
    }: {
      injects?: UrlInjectionMap;
      params?: Record<string, unknown>;
      config?: ExtendedAxiosRequestConfig;
      options?: UseGenericPaginatedQueryOptionsType<S>;
    } = {}
  ): UseInfiniteQueryResult<PaginatorContract<S>> & {
    hasNoResults: boolean;
  } {
    const axiosConfig = { ...config };
    const queryKey = queryKeyManager.generateKey(
      params ? [url, JSON.stringify(params)] : [url],
      injects
    );

    // Use createQueryOptions to create query options with correct types
    const queryOptions = createQueryOptions<
      UseInfiniteQueryOptions<
        PaginatorContract<S>,
        unknown,
        PaginatorContract<S>,
        PaginatorContract<S>,
        string[]
      >
    >({
      getNextPageParam(lastPage) {
        return lastPage?.next_page_number || undefined;
      },
      ...options,
    });

    const { isFetching, hasNextPage, fetchNextPage, ...rest } =
      useInfiniteQuery(
        queryKey,
        ({ pageParam = 1 }) => {
          const queryParams = { ...params, page: pageParam };
          return repository
            .request(repository.buildUrl(url, injects), {
              ...axiosConfig,
              params: queryParams,
            })
            .then((response) => response.data);
        },
        queryOptions
      );

    const hasNoResults = useMemo(
      () => !isFetching && rest.data?.pages[0]?.results?.length === 0,
      [isFetching, rest.data]
    );

    return { isFetching, hasNextPage, fetchNextPage, hasNoResults, ...rest };
  }

  /**
   * Retrieves specific field(s) from an entity, checking cache first then fetching from API if needed.
   * Supports extracting single fields or multiple fields from the cached/fetched data.
   * Use when you don't need data from the entire entity, but only specific fields.
   *
   * @example
   * // Single field
   * const { data: internalFees } = useRetrieveField(ticketTypeId, "venue_internal_fees", { options: { enabled: Boolean(ticketTypeId) } });
   *
   * @example
   * // Multiple fields
   * const { data: fields } = useRetrieveField(ticketTypeId, ["venue_internal_fees", "price", "name"]);
   * // Returns: { venue_internal_fees: ..., price: ..., name: ... }
   */
  function useRetrieveField<K extends keyof T>(
    id: Identifier | undefined,
    fields: K | K[],
    {
      config,
      options,
    }: QueryServiceOptionsFactory<
      K extends (keyof T)[] ? Pick<T, K[number]> : T[K]
    > = {}
  ) {
    const isEnabled = () => {
      if (Boolean(!!id && id !== "undefined") === false) {
        return false;
      }
      if (options && "enabled" in options) {
        return Boolean(options.enabled);
      }
      return true;
    };

    const fieldsArray = Array.isArray(fields) ? fields : [fields];
    const queryKey = queryKeyManager.generateKey([
      "retrieveField",
      String(id || "undefined"),
      fieldsArray.sort().join(","),
    ]);

    const queryOptions = createQueryOptions({
      enabled: isEnabled(),
      ...options,
    });

    const extractFields = (data: T, fields: K[]) =>
      fields.reduce((acc, field) => {
        acc[field] = data[field];
        return acc;
      }, {} as Pick<T, K>);

    return useQuery(
      queryKey,
      async () => {
        if (!id) {
          return null;
        }

        let result = null;

        const cacheKey = queryKeyManager.generateKey(["retrieve", id]);
        const cachedData = queryClient.getQueryData<T>(cacheKey);

        if (cachedData) {
          if (Array.isArray(fields)) {
            result = extractFields(cachedData, fields);
          } else {
            result = cachedData[fields as K];
          }
        } else {
          const apiData = await repository.retrieve(id, config);

          queryClient.setQueryData(cacheKey, apiData);

          if (Array.isArray(fields)) {
            result = extractFields(apiData, fields);
          } else {
            result = apiData[fields as K];
          }
        }

        return result;
      },
      queryOptions
    );
  }

  return {
    useList,
    useCreate,
    useRetrieve,
    useRetrieveField,
    useUpdate,
    useDelete,
    useGenericQuery,
    useGenericMutation,
    usePaginated,
    usePartialUpdate,
    useGenericPaginatedQuery,
    invalidateAllRepositoryQueries,
    invalidateSubcategoryQueries,
    invalidateQueriesByRepositoryNames,
    removeAllRepositoryQueries,
    queryKeyManager,
  };
}

export type UseModelServiceType<R extends Repository<unknown>> = ReturnType<
  typeof useModelService<R>
>;

export type QueryServiceOptionsFactory<T> = {
  injects?: UrlInjectionMap;
  params?: Record<string, unknown>;
  config?: ExtendedAxiosRequestConfig;
  options?: UseQueryOptions<T>;
};

export type PaginatedQueryServiceOptionsFactory<T> = {
  url?: "list" | "paginate" | string;
  injects?: UrlInjectionMap;
  params?: Record<string, unknown>;
  config?: ExtendedAxiosRequestConfig;
  options?: Omit<
    UseInfiniteQueryOptions<
      PaginatorContract<T>, // Data type
      unknown, // Error type
      PaginatorContract<T>, // Data type returned by the query function
      PaginatorContract<T>, // Data type returned by the query function
      string[] // Type of the query key
    >,
    "queryKey" | "queryFn"
  >;
};

export type MutationServiceOptionsFactory<TData, TVariables> = {
  injects?: UrlInjectionMap;
  config?: ExtendedAxiosRequestConfig;
  options?: UseMutationOptions<TData, unknown, TVariables>;
  mapVariablesToUrlInjects?: (variables: TVariables) => UrlInjectionMap;
  mapVariablesToRequestBody?: (variables: TVariables) => unknown;
  mapVariablesToParams?: (variables: TVariables) => Record<string, unknown>;
};
